// DEPLOY: /var/www/woody.wtf/frontend/src/pages/AdminDirMap.js
// Admin-only live directory map of the project (git-tracked files).
import React, { useCallback, useEffect, useState } from 'react';
import Navbar from '../components/Navbar';
import api from '../api';

const s = {
  page: { minHeight: '100vh', background: 'var(--bg)' },
  content: { maxWidth: '1200px', margin: '0 auto', padding: '40px 32px' },
  top: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' },
  heading: { fontSize: '24px', fontWeight: 600, letterSpacing: '-0.02em' },
  btn: {
    padding: '5px 12px', borderRadius: 'var(--radius)', border: '1px solid var(--border)',
    cursor: 'pointer', fontSize: '12px', fontWeight: 500, fontFamily: "'Space Grotesk', sans-serif",
  },
  section: {
    background: 'var(--surface)', border: '1px solid var(--border)',
    borderRadius: 'calc(var(--radius) * 2)', padding: '16px 24px', marginBottom: '16px',
  },
  det: { borderBottom: '1px solid var(--border)', padding: '8px 0' },
  sum: { cursor: 'pointer', display: 'flex', gap: '12px', alignItems: 'baseline', fontSize: '13px' },
  meta: { color: 'var(--text-muted)', fontSize: '12px', fontFamily: "'JetBrains Mono', monospace" },
  tbl: { width: '100%', borderCollapse: 'collapse', fontSize: '12px', marginTop: '8px' },
  th: {
    textAlign: 'left', padding: '4px 8px', borderBottom: '1px solid var(--border)',
    color: 'var(--text-muted)', fontWeight: 500, fontFamily: "'JetBrains Mono', monospace",
    fontSize: '10px', letterSpacing: '0.05em', textTransform: 'uppercase',
  },
  td: { padding: '4px 8px', borderBottom: '1px solid var(--border)', verticalAlign: 'top' },
  err: { color: 'var(--error)', fontSize: '13px', marginBottom: '12px' },
};

function fmtSize(b) {
  if (b == null) return '—';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(2)} MB`;
}

export default function AdminDirMap() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    setData(null);
    setErr('');
    try {
      const r = await api.get('/admin/dir-map');
      setData(r.data);
    } catch (e) {
      setErr(e.response?.data?.error || e.message);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const download = () => {
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `dir-map-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <div style={s.page}>
      <Navbar />
      <div style={s.content}>
        <div style={s.top}>
          <div>
            <div style={s.heading}>Directory Map</div>
            {data && (
              <span style={{ color: 'var(--text-muted)', fontSize: '13px' }}>
                {data.root} · {data.directories.reduce((n, d) => n + d.files.length, 0)} git-tracked files
              </span>
            )}
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={load} style={{ ...s.btn, background: 'var(--surface)', color: 'var(--text)' }}>Refresh</button>
            {data && <button onClick={download} style={{ ...s.btn, background: 'var(--surface)', color: 'var(--text)' }}>Download JSON</button>}
          </div>
        </div>
        {err && <div style={s.err}>{err}</div>}
        {!data && !err && <div style={{ color: 'var(--text-muted)' }}>Loading…</div>}
        {data && (
          <div style={s.section}>
            {data.directories.map(d => (
              <details key={d.path} style={s.det}>
                <summary style={s.sum}>
                  <span style={{ fontWeight: 600 }}>{d.path}</span>
                  <span style={s.meta}>{d.files.length} files</span>
                </summary>
                <table style={s.tbl}>
                  <thead>
                    <tr>
                      <th style={s.th}>File</th>
                      <th style={s.th}>Size</th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.files.map(f => (
                      <tr key={f.name}>
                        <td style={s.td}>{f.name}</td>
                        <td style={{ ...s.td, ...s.meta }}>{fmtSize(f.size)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </details>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
